import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
  Card,
  Row,
  Col,
  Timeline,
  Divider,
  Avatar,
} from "antd";
import {
  BankOutlined,
  SafetyOutlined,
  TeamOutlined,
  TrophyOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  HeartOutlined,
  LockOutlined,
  UserOutlined,
  HomeOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import { useTheme } from "../store/context/theme-context";
import { useTenant } from "../store/context/tenant-context";
import { useTranslation } from "react-i18next";

const { Title, Text, Paragraph } = Typography;

const CompanyProfile = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const tenant = useTenant();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");
  const [scrolled, setScrolled] = useState(false);

  const isDark = theme === "dark";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Company data - can be fetched from backend or tenant config
  const companyData = {
    name: tenant.name || "BankLingo",
    tagline: "Empowering Financial Innovation",
    description:
      "BankLingo is a leading financial technology platform that provides comprehensive banking solutions for modern financial institutions. We specialize in digital banking infrastructure, core banking systems, and innovative financial products that help banks and microfinance institutions serve their customers better.",
    founded: "2019",
    headquarters: "Lagos, Nigeria",
    ceo: "John Doe",
    employees: "100+",
    customers: "50+ Financial Institutions",
  };

  const mission = {
    title: "Our Mission",
    description:
      "To empower financial institutions across Africa with cutting-edge technology solutions that enable them to provide accessible, efficient, and secure banking services to everyone, everywhere.",
  };

  const vision = {
    title: "Our Vision",
    description:
      "To be Africa's leading banking technology platform, transforming how financial services are delivered and making banking accessible to every individual and business across the continent.",
  };

  const coreValues = [
    {
      icon: <CheckCircleOutlined />,
      title: "Integrity",
      description:
        "We maintain the highest standards of honesty and ethical conduct in all our dealings.",
      color: "#1890ff",
    },
    {
      icon: <RocketOutlined />,
      title: "Innovation",
      description:
        "We continuously push boundaries to create cutting-edge solutions that solve real problems.",
      color: "#52c41a",
    },
    {
      icon: <HeartOutlined />,
      title: "Customer Focus",
      description:
        "Our customers' success is our success. We listen, understand, and deliver exceptional value.",
      color: "#f5222d",
    },
    {
      icon: <LockOutlined />,
      title: "Security",
      description:
        "We prioritize the security and privacy of our customers' data with enterprise-grade protection.",
      color: "#fa8c16",
    },
    {
      icon: <TeamOutlined />,
      title: "Collaboration",
      description:
        "We believe in the power of teamwork and foster a culture of mutual support and respect.",
      color: "#722ed1",
    },
    {
      icon: <TrophyOutlined />,
      title: "Excellence",
      description:
        "We strive for excellence in everything we do, continuously improving and raising the bar.",
      color: "#13c2c2",
    },
  ];

  const milestones = [
    {
      year: "2019",
      title: "Company Founded",
      description: "BankLingo was established with a vision to revolutionize banking technology in Africa.",
    },
    {
      year: "2020",
      title: "First Major Client",
      description: "Onboarded our first microfinance bank, serving 10,000+ customers.",
    },
    {
      year: "2021",
      title: "Product Expansion",
      description: "Launched insurance and loan management modules, expanding our product suite.",
    },
    {
      year: "2022",
      title: "Multi-Tenancy Platform",
      description: "Released our multi-tenant platform, enabling white-label solutions for multiple banks.",
    },
    {
      year: "2023",
      title: "Regional Expansion",
      description: "Expanded operations to 5 African countries with 30+ financial institutions.",
    },
    {
      year: "2024",
      title: "AI Integration",
      description: "Integrated AI-powered analytics and fraud detection capabilities.",
    },
    {
      year: "2025",
      title: "50+ Institutions",
      description: "Reached milestone of serving over 50 financial institutions across Africa.",
    },
  ];

  const leadership = [
    {
      name: "John Doe",
      position: "Chief Executive Officer",
      image: null,
      bio: "John has over 15 years of experience in fintech and banking technology.",
    },
    {
      name: "Jane Smith",
      position: "Chief Technology Officer",
      image: null,
      bio: "Jane leads our technology vision with expertise in cloud architecture and security.",
    },
    {
      name: "Michael Johnson",
      position: "Chief Operating Officer",
      image: null,
      bio: "Michael oversees operations with a focus on operational excellence and customer success.",
    },
    {
      name: "Sarah Williams",
      position: "Chief Financial Officer",
      image: null,
      bio: "Sarah brings strategic financial leadership and expertise in scaling technology companies.",
    },
  ];

  const certifications = [
    {
      icon: <SafetyOutlined />,
      title: "ISO 27001 Certified",
      description: "Information Security Management",
    },
    {
      icon: <LockOutlined />,
      title: "PCI DSS Compliant",
      description: "Payment Card Industry Data Security Standard",
    },
    {
      icon: <BankOutlined />,
      title: "CBN Licensed",
      description: "Central Bank of Nigeria Approval",
    },
    {
      icon: <GlobalOutlined />,
      title: "SOC 2 Type II",
      description: "Service Organization Control",
    },
  ];

  const stats = [
    { value: "50+", label: "Financial Institutions", icon: <BankOutlined /> },
    { value: "500K+", label: "End Users", icon: <TeamOutlined /> },
    { value: "5", label: "Countries", icon: <GlobalOutlined /> },
    { value: "99.9%", label: "Uptime", icon: <SafetyOutlined /> },
  ];

  const renderOverview = () => (
    <div>
      {/* Hero Section */}
      <Card
        style={{
          marginBottom: "32px",
          borderRadius: "16px",
          border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
          background: isDark
            ? `linear-gradient(135deg, ${tenant.theme.colorPrimary}15 0%, ${tenant.theme.colorPrimary}08 100%)`
            : `linear-gradient(135deg, ${tenant.theme.colorPrimary}10 0%, ${tenant.theme.colorPrimary}05 100%)`,
        }}
      >
        <Row gutter={[32, 32]} align="middle">
          <Col xs={24} md={12}>
            <Title
              level={1}
              style={{
                color: isDark ? "#ffffff" : "#000000",
                marginBottom: "16px",
                fontSize: "42px",
              }}
            >
              {companyData.name}
            </Title>
            <Title
              level={3}
              style={{
                color: tenant.theme.colorPrimary,
                marginBottom: "24px",
                fontWeight: 500,
              }}
            >
              {companyData.tagline}
            </Title>
            <Paragraph
              style={{
                fontSize: "16px",
                lineHeight: "1.8",
                color: isDark ? "#e0e0e0" : "#666666",
                marginBottom: "24px",
              }}
            >
              {companyData.description}
            </Paragraph>
            <Button
              type="primary"
              size="large"
              onClick={() => navigate("/contact")}
              style={{
                backgroundColor: tenant.theme.colorPrimary,
                borderColor: tenant.theme.colorPrimary,
                height: "48px",
                fontSize: "16px",
                fontWeight: 600,
                paddingLeft: "32px",
                paddingRight: "32px",
              }}
            >
              Get In Touch
            </Button>
          </Col>
          <Col xs={24} md={12}>
            <Row gutter={[16, 16]}>
              {stats.map((stat, index) => (
                <Col xs={12} key={index}>
                  <Card
                    style={{
                      textAlign: "center",
                      borderRadius: "12px",
                      border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
                      backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "32px",
                        color: tenant.theme.colorPrimary,
                        marginBottom: "8px",
                      }}
                    >
                      {stat.icon}
                    </div>
                    <Title
                      level={2}
                      style={{
                        color: isDark ? "#ffffff" : "#000000",
                        marginBottom: "4px",
                        fontSize: "32px",
                      }}
                    >
                      {stat.value}
                    </Title>
                    <Text
                      style={{
                        fontSize: "14px",
                        color: isDark ? "#999999" : "#666666",
                      }}
                    >
                      {stat.label}
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </Card>

      {/* Mission & Vision */}
      <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
        <Col xs={24} md={12}>
          <Card
            style={{
              height: "100%",
              borderRadius: "16px",
              border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
              backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
            }}
          >
            <div
              style={{
                fontSize: "48px",
                color: tenant.theme.colorPrimary,
                marginBottom: "16px",
              }}
            >
              🎯
            </div>
            <Title
              level={3}
              style={{
                color: isDark ? "#ffffff" : "#000000",
                marginBottom: "16px",
              }}
            >
              {mission.title}
            </Title>
            <Paragraph
              style={{
                fontSize: "16px",
                lineHeight: "1.8",
                color: isDark ? "#e0e0e0" : "#666666",
              }}
            >
              {mission.description}
            </Paragraph>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            style={{
              height: "100%",
              borderRadius: "16px",
              border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
              backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
            }}
          >
            <div
              style={{
                fontSize: "48px",
                color: tenant.theme.colorPrimary,
                marginBottom: "16px",
              }}
            >
              🔭
            </div>
            <Title
              level={3}
              style={{
                color: isDark ? "#ffffff" : "#000000",
                marginBottom: "16px",
              }}
            >
              {vision.title}
            </Title>
            <Paragraph
              style={{
                fontSize: "16px",
                lineHeight: "1.8",
                color: isDark ? "#e0e0e0" : "#666666",
              }}
            >
              {vision.description}
            </Paragraph>
          </Card>
        </Col>
      </Row>

      {/* Core Values */}
      <Card
        style={{
          marginBottom: "32px",
          borderRadius: "16px",
          border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
          backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
        }}
      >
        <Title
          level={2}
          style={{
            color: isDark ? "#ffffff" : "#000000",
            marginBottom: "32px",
            textAlign: "center",
          }}
        >
          Our Core Values
        </Title>
        <Row gutter={[24, 24]}>
          {coreValues.map((value, index) => (
            <Col xs={24} sm={12} lg={8} key={index}>
              <div
                style={{
                  textAlign: "center",
                  padding: "24px",
                  borderRadius: "12px",
                  border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
                  backgroundColor: isDark ? "#141414" : "#fafafa",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    fontSize: "48px",
                    color: value.color,
                    marginBottom: "16px",
                  }}
                >
                  {value.icon}
                </div>
                <Title
                  level={4}
                  style={{
                    color: isDark ? "#ffffff" : "#000000",
                    marginBottom: "12px",
                  }}
                >
                  {value.title}
                </Title>
                <Paragraph
                  style={{
                    fontSize: "14px",
                    color: isDark ? "#999999" : "#666666",
                    lineHeight: "1.6",
                  }}
                >
                  {value.description}
                </Paragraph>
              </div>
            </Col>
          ))}
        </Row>
      </Card>
    </div>
  );

  const renderHistory = () => (
    <Card
      style={{
        marginBottom: "32px",
        borderRadius: "16px",
        border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
        backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
        padding: "24px",
      }}
    >
      <Title
        level={2}
        style={{
          color: isDark ? "#ffffff" : "#000000",
          marginBottom: "32px",
          textAlign: "center",
        }}
      >
        Our Journey
      </Title>
      <Timeline
        mode="alternate"
        style={{ paddingTop: "24px" }}
        items={milestones.map((milestone, index) => ({
          color: tenant.theme.colorPrimary,
          children: (
            <Card
              style={{
                borderRadius: "12px",
                border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
                backgroundColor: isDark ? "#141414" : "#fafafa",
              }}
            >
              <Title
                level={4}
                style={{
                  color: tenant.theme.colorPrimary,
                  marginBottom: "8px",
                }}
              >
                {milestone.year}
              </Title>
              <Title
                level={5}
                style={{
                  color: isDark ? "#ffffff" : "#000000",
                  marginBottom: "8px",
                }}
              >
                {milestone.title}
              </Title>
              <Paragraph
                style={{
                  fontSize: "14px",
                  color: isDark ? "#999999" : "#666666",
                  marginBottom: 0,
                }}
              >
                {milestone.description}
              </Paragraph>
            </Card>
          ),
        }))}
      />
    </Card>
  );

  const renderLeadership = () => (
    <Card
      style={{
        marginBottom: "32px",
        borderRadius: "16px",
        border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
        backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
      }}
    >
      <Title
        level={2}
        style={{
          color: isDark ? "#ffffff" : "#000000",
          marginBottom: "32px",
          textAlign: "center",
        }}
      >
        Leadership Team
      </Title>
      <Row gutter={[24, 24]}>
        {leadership.map((leader, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <div style={{ textAlign: "center" }}>
              <Avatar
                size={120}
                icon={<UserOutlined />}
                style={{
                  backgroundColor: tenant.theme.colorPrimary,
                  marginBottom: "16px",
                }}
              />
              <Title
                level={4}
                style={{
                  color: isDark ? "#ffffff" : "#000000",
                  marginBottom: "4px",
                }}
              >
                {leader.name}
              </Title>
              <Text
                style={{
                  fontSize: "14px",
                  color: tenant.theme.colorPrimary,
                  display: "block",
                  marginBottom: "12px",
                  fontWeight: 600,
                }}
              >
                {leader.position}
              </Text>
              <Paragraph
                style={{
                  fontSize: "13px",
                  color: isDark ? "#999999" : "#666666",
                  lineHeight: "1.6",
                }}
              >
                {leader.bio}
              </Paragraph>
            </div>
          </Col>
        ))}
      </Row>
    </Card>
  );

  const renderCertifications = () => (
    <div>
      <Card
        style={{
          marginBottom: "32px",
          borderRadius: "16px",
          border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
          backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
        }}
      >
        <Title
          level={2}
          style={{
            color: isDark ? "#ffffff" : "#000000",
            marginBottom: "32px",
            textAlign: "center",
          }}
        >
          Licenses & Certifications
        </Title>
        <Row gutter={[24, 24]}>
          {certifications.map((cert, index) => (
            <Col xs={24} sm={12} key={index}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  padding: "24px",
                  borderRadius: "12px",
                  border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
                  backgroundColor: isDark ? "#141414" : "#fafafa",
                }}
              >
                <div
                  style={{
                    fontSize: "48px",
                    color: tenant.theme.colorPrimary,
                  }}
                >
                  {cert.icon}
                </div>
                <div>
                  <Title
                    level={4}
                    style={{
                      color: isDark ? "#ffffff" : "#000000",
                      marginBottom: "4px",
                    }}
                  >
                    {cert.title}
                  </Title>
                  <Text
                    style={{
                      fontSize: "14px",
                      color: isDark ? "#999999" : "#666666",
                    }}
                  >
                    {cert.description}
                  </Text>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Contact Information */}
      <Card
        style={{
          borderRadius: "16px",
          border: `1px solid ${isDark ? "#303030" : "#e8e8e8"}`,
          backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
        }}
      >
        <Title
          level={2}
          style={{
            color: isDark ? "#ffffff" : "#000000",
            marginBottom: "32px",
            textAlign: "center",
          }}
        >
          Get In Touch
        </Title>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "32px",
                  color: tenant.theme.colorPrimary,
                  marginBottom: "12px",
                }}
              >
                <EnvironmentOutlined />
              </div>
              <Title
                level={5}
                style={{ color: isDark ? "#ffffff" : "#000000" }}
              >
                Address
              </Title>
              <Text style={{ color: isDark ? "#999999" : "#666666" }}>
                {companyData.headquarters}
              </Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "32px",
                  color: tenant.theme.colorPrimary,
                  marginBottom: "12px",
                }}
              >
                <MailOutlined />
              </div>
              <Title
                level={5}
                style={{ color: isDark ? "#ffffff" : "#000000" }}
              >
                Email
              </Title>
              <Text style={{ color: isDark ? "#999999" : "#666666" }}>
                info@{(tenant?.name || companyData.name || "banklingo").toLowerCase()}.com
              </Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "32px",
                  color: tenant.theme.colorPrimary,
                  marginBottom: "12px",
                }}
              >
                <PhoneOutlined />
              </div>
              <Title
                level={5}
                style={{ color: isDark ? "#ffffff" : "#000000" }}
              >
                Phone
              </Title>
              <Text style={{ color: isDark ? "#999999" : "#666666" }}>
                +234 (0) 123 456 7890
              </Text>
            </div>
          </Col>
        </Row>
        <Divider />
        <div style={{ textAlign: "center" }}>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate("/contact")}
            style={{
              backgroundColor: tenant.theme.colorPrimary,
              borderColor: tenant.theme.colorPrimary,
              height: "48px",
              fontSize: "16px",
              fontWeight: 600,
              paddingLeft: "32px",
              paddingRight: "32px",
            }}
          >
            Contact Us
          </Button>
        </div>
      </Card>
    </div>
  );

  const tabs = [
    { key: "overview", label: "Overview", icon: <BankOutlined /> },
    { key: "history", label: "Our History", icon: <TrophyOutlined /> },
    { key: "leadership", label: "Leadership", icon: <TeamOutlined /> },
    { key: "certifications", label: "Certifications", icon: <SafetyOutlined /> },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: isDark ? "#0a0a0a" : "#f5f7fa",
      }}
    >
      {/* Fixed Header Navigation */}
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: scrolled
            ? isDark
              ? "rgba(20, 20, 20, 0.95)"
              : "rgba(255, 255, 255, 0.95)"
            : isDark
            ? "rgba(20, 20, 20, 0.8)"
            : "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(10px)",
          borderBottom: `1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          }`,
          transition: "all 0.3s ease",
          boxShadow: scrolled
            ? isDark
              ? "0 2px 8px rgba(0, 0, 0, 0.5)"
              : "0 2px 8px rgba(0, 0, 0, 0.1)"
            : "none",
        }}
      >
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Logo and Brand */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              cursor: "pointer",
            }}
            onClick={() => navigate("/landing")}
          >
            <img
              src={tenant.logo}
              alt={tenant.name}
              style={{ height: "40px", width: "auto" }}
            />
            <Title
              level={4}
              style={{
                margin: 0,
                color: isDark ? "#ffffff" : "#000000",
                fontSize: "20px",
                fontWeight: 700,
              }}
            >
              {companyData.name}
            </Title>
          </div>

          {/* Navigation Buttons */}
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <Button
              icon={<HomeOutlined />}
              onClick={() => navigate("/landing")}
              style={{
                height: "40px",
                fontSize: "15px",
                fontWeight: 600,
                borderColor: isDark ? "#303030" : "#e8e8e8",
                color: isDark ? "#ffffff" : "#000000",
                backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
              }}
            >
              Home
            </Button>
            <Button
              type="primary"
              onClick={() => navigate("/contact")}
              style={{
                backgroundColor: tenant.theme.colorPrimary,
                borderColor: tenant.theme.colorPrimary,
                height: "40px",
                fontSize: "15px",
                fontWeight: 600,
              }}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "100px 24px 24px" }}>
        {/* Back Button */}
        {/* <div style={{ marginBottom: "32px" }}>
          <Button
            icon={<LeftOutlined />}
            onClick={() => navigate(-1)}
            style={{
              color: tenant.theme.colorPrimary,
              borderColor: tenant.theme.colorPrimary,
              fontWeight: 600,
              height: "40px",
            }}
          >
            Back
          </Button>
        </div> */}

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "48px" }}>
          <Title
            level={1}
            style={{
              color: isDark ? "#ffffff" : "#000000",
              marginBottom: "16px",
              fontSize: "42px",
            }}
          >
            Company Profile
          </Title>
          <Paragraph
            style={{
              fontSize: "18px",
              color: isDark ? "#999999" : "#666666",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Learn more about who we are, what we do, and how we're transforming
            banking in Africa
          </Paragraph>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            marginBottom: "32px",
            flexWrap: "wrap",
          }}
        >
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              size="large"
              icon={tab.icon}
              onClick={() => setActiveTab(tab.key)}
              style={{
                height: "48px",
                fontSize: "16px",
                fontWeight: 600,
                borderRadius: "24px",
                paddingLeft: "24px",
                paddingRight: "24px",
                backgroundColor:
                  activeTab === tab.key
                    ? tenant.theme.colorPrimary
                    : isDark
                    ? "#1f1f1f"
                    : "#ffffff",
                color:
                  activeTab === tab.key
                    ? "#ffffff"
                    : isDark
                    ? "#ffffff"
                    : "#000000",
                borderColor:
                  activeTab === tab.key
                    ? tenant.theme.colorPrimary
                    : isDark
                    ? "#303030"
                    : "#e8e8e8",
              }}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Content */}
        <div>
          {activeTab === "overview" && renderOverview()}
          {activeTab === "history" && renderHistory()}
          {activeTab === "leadership" && renderLeadership()}
          {activeTab === "certifications" && renderCertifications()}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;
